import { useMsal } from '@azure/msal-react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Checkbox,
  CssBaseline,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import Logout from '@mui/icons-material/Logout';
import { Popper } from '@mui/material';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import clsx from 'clsx';
import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { Maybe, useGetClientAppVersionQuery } from '../generated/graphql';
import { useAccess } from '../lib/useAccess';
import { getUserInitials, getUserName } from '../lib/useRoles';
import { useUserConfiguration } from '../providers/UserConfigurationProvider';
import { DialogModal } from './DialogModal';
import { BookmarksModal } from './modal/BookmarksModal';
import { ReactComponent as BoxIcon } from '../svgIcons/goodsbox.svg';
import { makeStyles, createStyles } from '@mui/styles';
import UpdateIcon from '@mui/icons-material/Update';
import packageJson from '../../package.json';
import {
  TERMINAL_STATUS_OPTIONS,
  getTerminalStatusSelections,
  getDoneStatusSelections,
} from '../utils/terminalStatusOptions';

const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    '@global': {
      '*::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '*::-webkit-scrollbar-track': {
        '-webkit-box-shadow': 'inset 0 0 3px rgba(0,0,0,0.1)',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(104,188,70,.2)',
      },
      '& .MuiDataGrid-virtualScroller': {
        '&::-webkit-scrollbar': {
          width: '15px',
          height: '15px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#68bc46',
          borderRadius: 8,
          minWidth: '25px',
          minHeight: '40px',
        },
        scrollbarWidth: '15px', // For Firefox
        scrollbarColor: '#7ac47brgb(104, 188, 70)', // For Firefox
      },
    },
    root: {
      display: 'flex',
      height: '100%',
    },
    appBar: {
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      backgroundColor: '#68BC46',
    },
    appBarShift: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    active: {
      color: '#69bc46',
    },
    hide: {
      display: 'none',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-end',
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(1.5, 3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginLeft: -drawerWidth,
      maxHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
    },
    contentShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      maxWidth: `calc(100% - ${drawerWidth}px)`,
    },
    childContent: {
      position: 'relative',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    updateButton: {
      position: 'relative',
      backgroundColor: 'rgba(255,255,255,0.12)',
      animation: '$blink 1.1s ease-in-out infinite',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
      },
    },
    updateBadge: {
      position: 'absolute',
      top: 6,
      right: 5,
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: '#ff3b30',
      border: '2px solid #fff',
      boxShadow: '0 0 0 2px rgba(255,59,48,0.2)',
      zIndex: 1,
    },
    '@keyframes blink': {
      '0%, 100%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '50%': {
        opacity: 0.55,
        transform: 'scale(1.08)',
      },
    },
  }),
);

interface DrawerLink {
  text: string;
  path: string;
  Icon?: any;
}

interface LayoutProps {
  pageName?: string;
  drawerLinks?: Maybe<DrawerLink>[];
  children: NonNullable<ReactNode>;
}

const TERMINAL_STATUS_SETTINGS_TABLE = 'terminalStatusSettings';
const TERMINAL_STATUS_SETTINGS_KEY = 'selectedStatuses';
const TERMINAL_STATUS_DONE_KEY = 'doneStatuses';

type ConfirmationAction = 'clearBrowserData' | 'clearUserConfiguration' | null;

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function Layout(props: LayoutProps) {
  const { drawerLinks } = props;
  const classes = useStyles({});
  const url = window.location.href;
  const isCustomer = url.includes('customer');
  const hasDrawerLinks = drawerLinks != null;
  const links: DrawerLink[] = hasDrawerLinks
    ? (drawerLinks || []).filter(notEmpty)
    : [];
  const [drawerOpen, setDrawerOpen] = useState(hasDrawerLinks);
  const theme = useTheme();
  const { terminal, traffic, customsOfficer } = useAccess();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuId = 'account-menu';
  const isMenuOpen = Boolean(anchorEl);
  const history = useHistory();
  const { t } = useTranslation();
  const {
    clearConfiguration,
    getCustomConfiguration,
    updateConfiguration,
    globalDateFormat,
    setGlobalDateFormat,
    location,
  } = useUserConfiguration();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [confirmationAction, setConfirmationAction] =
    useState<ConfirmationAction>(null);
  const [terminalStatusSettingsOpen, setTerminalStatusSettingsOpen] =
    useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectTerminal = () => {
    history.push('/terminal/change-terminal');
    setAnchorEl(null);
  };

  const handleClearUserConfiguration = () => {
    clearConfiguration();
    setAnchorEl(null);
  };

  const handleBrowserClearData = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setConfirmationAction(null);
  };

  const handleOpenDeleteDialog = (
    action: Exclude<ConfirmationAction, null>,
    message: string,
  ) => {
    setConfirmationAction(action);
    setDialogMessage(message);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogAction = () => {
    if (confirmationAction === 'clearBrowserData') {
      handleBrowserClearData();
      return;
    }

    if (confirmationAction === 'clearUserConfiguration') {
      handleClearUserConfiguration();
    }
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const user = getUserName();
  const latestAppClientVersion = useGetClientAppVersionQuery();
  const localVersionOnUserSystem = packageJson.version;

  const { pathname } = useLocation();
  const { instance } = useMsal();
  const [checked, setChecked] = useState(
    localStorage.getItem('collapseTruckSections_' + user) === 'true'
      ? true
      : false,
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    localStorage.setItem(
      'collapseTruckSections_' + user,
      JSON.stringify(event.target.checked),
    );
    setAnchorEl(null);
  };
  const [anchorE2, setAnchorE2] = useState<null | HTMLElement>(null);
  const [showUpdateButton, setShowUpdateButton] = useState<boolean>(false);
  const configuredTerminalStatuses = getCustomConfiguration(
    TERMINAL_STATUS_SETTINGS_TABLE,
    TERMINAL_STATUS_SETTINGS_KEY,
  );
  const configuredDoneStatuses = getCustomConfiguration(
    TERMINAL_STATUS_SETTINGS_TABLE,
    TERMINAL_STATUS_DONE_KEY,
  );
  const [selectedTerminalStatuses, setSelectedTerminalStatuses] = useState<
    string[]
  >(getTerminalStatusSelections(configuredTerminalStatuses));
  const [selectedDoneStatuses, setSelectedDoneStatuses] = useState<string[]>(
    getDoneStatusSelections(configuredDoneStatuses),
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorE2(anchorE2 ? null : event.currentTarget);
  };

  useEffect(() => {
    const latestVersion = latestAppClientVersion.data?.settings[0]?.version;
    if (latestVersion && localVersionOnUserSystem) {
      setShowUpdateButton(latestVersion != localVersionOnUserSystem);
    }
  }, [latestAppClientVersion, localVersionOnUserSystem]);

  useEffect(() => {
    if (!terminalStatusSettingsOpen) {
      setSelectedTerminalStatuses(
        getTerminalStatusSelections(configuredTerminalStatuses),
      );
      setSelectedDoneStatuses(getDoneStatusSelections(configuredDoneStatuses));
    }
  }, [
    configuredTerminalStatuses,
    configuredDoneStatuses,
    terminalStatusSettingsOpen,
  ]);

  const handleOpenTerminalStatusSettings = () => {
    setSelectedTerminalStatuses(
      getTerminalStatusSelections(configuredTerminalStatuses),
    );
    setSelectedDoneStatuses(
      getDoneStatusSelections(
        (configuredDoneStatuses as Record<string, string[]>)?.[
          String(location?.id)
        ] ?? [],
      ),
    );

    setTerminalStatusSettingsOpen(true);
    setAnchorEl(null);
  };

  const handleToggleTerminalStatus = (status: string) => {
    setSelectedTerminalStatuses((previousStatuses) =>
      previousStatuses.includes(status)
        ? previousStatuses.filter((item) => item !== status)
        : [...previousStatuses, status],
    );
  };

  const handleToggleDoneStatus = (status: string) => {
    setSelectedDoneStatuses((previousStatuses) =>
      previousStatuses.includes(status)
        ? previousStatuses.filter((item) => item !== status)
        : [...previousStatuses, status],
    );
  };

  const handleSaveTerminalStatusSettings = () => {
    const nextStatuses = TERMINAL_STATUS_OPTIONS.map(
      (option) => option.value,
    ).filter((status) => selectedTerminalStatuses.includes(status));

    const nextDoneStatuses = TERMINAL_STATUS_OPTIONS.map(
      (option) => option.value,
    ).filter((status) => selectedDoneStatuses.includes(status));

    const existingDoneStatuses =
      configuredDoneStatuses &&
      typeof configuredDoneStatuses === 'object' &&
      !Array.isArray(configuredDoneStatuses)
        ? (configuredDoneStatuses as Record<string, string[]>)
        : {};

    const updatedDoneStatuses = {
      ...existingDoneStatuses,
      [String(location?.id)]: nextDoneStatuses,
    };

    updateConfiguration(TERMINAL_STATUS_SETTINGS_TABLE, 'custom', {
      [TERMINAL_STATUS_SETTINGS_KEY]: nextStatuses,
      [TERMINAL_STATUS_DONE_KEY]: updatedDoneStatuses,
    });

    setTerminalStatusSettingsOpen(false);
  };

  const renderMenu = (
    <>
      <Menu
        anchorEl={anchorEl}
        id={menuId}
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem disabled>{user}</MenuItem>
        <DialogModal
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          contentText={dialogMessage}
          doAction={handleDeleteDialogAction}
          closeAction={handleCloseDeleteDialog}
          buttonText={t('button.continue')}
        />
        <Divider />
        {terminal && !isCustomer ? (
          <MenuItem onClick={handleSelectTerminal}>
            {t('menu.changeTerminal')}
          </MenuItem>
        ) : null}
        {terminal ? (
          <MenuItem onClick={handleOpenTerminalStatusSettings}>
            Terminal Status Settings
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            handleOpenDeleteDialog(
              'clearUserConfiguration',
              t('dialog.userConfig'),
            );
          }}
        >
          {t('menu.clearConfig')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenDeleteDialog(
              'clearBrowserData',
              t('dialog.deleteBrowserConfigData'),
            );
          }}
        >
          {t('menu.clearBrowserConfig')}
        </MenuItem>
        {terminal ? (
          <MenuItem>
            {`${t('menu.collapseTruckSections')}`}

            <Switch
              checked={checked}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </MenuItem>
        ) : null}
        <MenuItem>
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                {'Date Format'}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={globalDateFormat}
                label="Date Time Format"
                onChange={(event: SelectChangeEvent) => {
                  updateConfiguration(
                    'userDateTimeFormatSetting',
                    'globalDateFormat',
                    event.target.value as string,
                  );
                  setGlobalDateFormat(event.target.value as string);
                  window.location.reload();
                }}
              >
                <MenuItem value={'yyyy-MM-dd HH:mm'}>{'yyyy-MM-dd'}</MenuItem>
                <MenuItem value={'dd-MM-yyyy HH:mm'}>{'dd-MM-yyyy'}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            localStorage.clear();
            instance.logoutRedirect();
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('button.logOut')}
        </MenuItem>
        <MenuItem>{`App Version -${packageJson.version}`}</MenuItem>
      </Menu>
      <Dialog
        open={terminalStatusSettingsOpen}
        onClose={() => setTerminalStatusSettingsOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Terminal Status Settings</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the terminal statuses that should be available in the status
            popup and which ones should control the DONE button.
            {'\n'}DONE means that when these status is applied the row will hide
            if HIDE DONE is activated.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 'bold' }}
              >
                SHOW
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        selectedTerminalStatuses.length ===
                        TERMINAL_STATUS_OPTIONS.length
                      }
                      indeterminate={
                        selectedTerminalStatuses.length > 0 &&
                        selectedTerminalStatuses.length <
                          TERMINAL_STATUS_OPTIONS.length
                      }
                      onChange={() => {
                        if (
                          selectedTerminalStatuses.length ===
                          TERMINAL_STATUS_OPTIONS.length
                        ) {
                          setSelectedTerminalStatuses([]);
                        } else {
                          setSelectedTerminalStatuses(
                            TERMINAL_STATUS_OPTIONS.map((o) => o.value),
                          );
                        }
                      }}
                    />
                  }
                  label="SELECT ALL"
                />
                {TERMINAL_STATUS_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={selectedTerminalStatuses.includes(
                          option.value,
                        )}
                        onChange={() =>
                          handleToggleTerminalStatus(option.value)
                        }
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 'bold' }}
              >
                DONE
              </Typography>
              <FormGroup>
                {TERMINAL_STATUS_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={selectedDoneStatuses.includes(option.value)}
                        onChange={() => handleToggleDoneStatus(option.value)}
                        sx={{
                          '&.Mui-checked': {
                            color: '#69bc46',
                          },
                        }}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminalStatusSettingsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTerminalStatusSettings}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: drawerOpen,
        })}
      >
        <Toolbar>
          {drawerLinks && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              className={clsx(classes.menuButton, drawerOpen && classes.hide)}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Grid container>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" noWrap align="left">
                Nexus{props.pageName ? ` - ${props.pageName}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Grid container justifyContent="flex-end" alignItems="center">
                {traffic && pathname && pathname.startsWith('/traffic') && (
                  <Tooltip
                    title={`${t('actions.viewItem', {
                      item: t('resource.bookmark.plural'),
                    })}`}
                  >
                    <IconButton onClick={handleClick}>
                      <BookmarksIcon color="action" />
                    </IconButton>
                  </Tooltip>
                )}
                {/* only visible when application version is mismatched when compare to client */}
                {showUpdateButton && (
                  <Tooltip
                    title="Nexus application has a new update. Please click on this icon to hard refresh!"
                    placement="bottom"
                  >
                    <IconButton
                      className={classes.updateButton}
                      onClick={() => {
                        handleOpenDeleteDialog(
                          'clearBrowserData',
                          t('dialog.deleteBrowserConfigData'),
                        );
                      }}
                    >
                      <Box className={classes.updateBadge} />
                      <UpdateIcon color="inherit" />
                    </IconButton>
                  </Tooltip>
                )}

                {terminal && (
                  <Button
                    style={{ fontSize: '12px' }}
                    color="inherit"
                    component={RouterLink}
                    to="/terminal"
                  >
                    {t('resource.terminal.capitalized')}
                  </Button>
                )}
                {traffic && (
                  <Button
                    style={{ fontSize: '12px' }}
                    color="inherit"
                    component={RouterLink}
                    to="/traffic"
                  >
                    {t('resource.traffic.capitalized')}
                  </Button>
                )}
                {customsOfficer && (
                  <Button
                    style={{ fontSize: '12px' }}
                    color="inherit"
                    component={RouterLink}
                    to="/customs"
                  >
                    {t('resource.customs.capitalized')}
                  </Button>
                )}
                <Button
                  style={{ fontSize: '12px' }}
                  color="inherit"
                  component={RouterLink}
                  to="/customer"
                >
                  {t('resource.customer.capitalized')}
                </Button>
                <Divider
                  style={{ marginLeft: '5px', marginRight: '5px' }}
                  orientation="vertical"
                  flexItem
                />
                <IconButton
                  aria-label="account of current user"
                  aria-controls={menuId}
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                >
                  <Avatar
                    style={{
                      height: '30px',
                      width: '30px',
                      fontSize: '15px',
                    }}
                    alt={user}
                  >
                    {getUserInitials()}
                  </Avatar>
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {traffic && pathname && pathname.startsWith('/traffic') && (
        <Popper
          id={'Bookmarks-popper'}
          open={Boolean(anchorE2)}
          anchorEl={anchorE2}
        >
          <BookmarksModal />
        </Popper>
      )}
      {renderMenu}
      {props.drawerLinks && (
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div className={classes.drawerHeader}>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </div>
          <Divider />
          <List>
            {links.map(({ text, path, Icon }) => (
              <Fragment key={text}>
                <ListItem button component={RouterLink} to={path}>
                  {Icon && (
                    <ListItemIcon
                      className={pathname === path ? classes.active : ''}
                    >
                      <Icon />
                    </ListItemIcon>
                  )}
                  {text === 'Remaining Goods' && (
                    <ListItemIcon
                      className={pathname === path ? classes.active : ''}
                    >
                      <BoxIcon
                        fill={pathname === path ? '#69bc46' : 'grey'}
                        height={34}
                        width={34}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={text}
                    className={pathname === path ? classes.active : ''}
                  />
                </ListItem>
                {text === 'Report to traffic' && (
                  <Divider
                    orientation="horizontal"
                    style={{ height: 3, width: '100%', marginTop: 40 }}
                  />
                )}
              </Fragment>
            ))}
          </List>
        </Drawer>
      )}
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: drawerOpen || !hasDrawerLinks,
        })}
      >
        <div className={classes.drawerHeader} />
        <div className={classes.childContent}>{props.children}</div>
      </main>
    </div>
  );
}
